import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Loader2, RefreshCw } from "lucide-react";
import { api, type PublishedReel } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { MEDIA_THUMB_CLASS, MEDIA_THUMB_FRAME, MEDIA_THUMB_LIMIT_STYLE } from "@/components/MediaThumb";
import { cn } from "@/lib/utils";

const GRAPH_LIMIT = 150;

function dayLabel(iso?: string) {
  if (!iso) return "";
  const d = iso.slice(0, 10);
  return d || "";
}

export default function IgReelImportPanel({
  className,
  onImported,
  onGoToLibrary,
}: {
  className?: string;
  onImported?: () => void;
  onGoToLibrary?: () => void;
}) {
  const qc = useQueryClient();
  const [bulkLog, setBulkLog] = useState<string>("");
  const graphQ = useQuery({
    queryKey: ["media-reels-published", GRAPH_LIMIT],
    queryFn: () => api.mediaReelsPublished({ limit: GRAPH_LIMIT }),
  });
  const importM = useMutation({
    mutationFn: (id: string) => api.mediaReelImport(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media"] });
      qc.invalidateQueries({ queryKey: ["media-reels-published"] });
      qc.invalidateQueries({ queryKey: ["media-reels-library"] });
      onImported?.();
    },
  });
  const bulkM = useMutation({
    mutationFn: async (ids: string[]) => {
      let ok = 0;
      let skip = 0;
      let fail = 0;
      const errors: string[] = [];
      for (const id of ids) {
        try {
          const res = await api.mediaReelImport(id);
          if (res.created) ok += 1;
          else skip += 1;
        } catch (err) {
          fail += 1;
          errors.push(`${id}: ${err instanceof Error ? err.message : "błąd"}`);
        }
      }
      return { ok, skip, fail, errors };
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["media"] });
      qc.invalidateQueries({ queryKey: ["media-reels-published"] });
      qc.invalidateQueries({ queryKey: ["media-reels-library"] });
      const parts = [`nowe ${res.ok}`, `już były ${res.skip}`, `błąd ${res.fail}`];
      setBulkLog(parts.join(" · ") + (res.errors[0] ? ` · ${res.errors[0]}` : ""));
      if (res.ok > 0) onImported?.();
    },
  });

  const items: PublishedReel[] = graphQ.data?.items || [];
  const pending = useMemo(
    () => items.filter((row) => row.downloadable && !row.already_in_library),
    [items],
  );
  const already = useMemo(() => items.filter((row) => row.already_in_library), [items]);
  const blocked = useMemo(
    () => items.filter((row) => !row.already_in_library && !row.downloadable),
    [items],
  );
  const busy = importM.isPending || bulkM.isPending;

  return (
    <div className={cn("rounded-lg border border-border bg-muted/20 p-3", className)}>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <h2 className="text-sm font-medium">Import rolek z Instagram</h2>
        <span className="text-[11px] text-muted-foreground">Graph API, własne publikacje</span>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={graphQ.isFetching || busy}
            onClick={() => void graphQ.refetch()}
          >
            {graphQ.isFetching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Odśwież
          </Button>
          <Button
            size="sm"
            variant="brand"
            disabled={!pending.length || busy}
            onClick={() => bulkM.mutate(pending.map((row) => row.id))}
          >
            {bulkM.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Download className="h-3.5 w-3.5" />
            )}
            Pobierz dostępne ({pending.length})
          </Button>
          {onGoToLibrary ? (
            <Button size="sm" variant="outline" onClick={onGoToLibrary}>
              Pobrane wideo
            </Button>
          ) : null}
        </div>
      </div>

      <p className="mb-3 text-[11px] text-muted-foreground">
        Do {GRAPH_LIMIT} najnowszych rolek z Graph (nie cały archiwum). Tylko te z plikiem do pobrania. Muzyka IG często
        blokuje URL: wtedy wgraj MP4 / inbox/rolki. Po pobraniu szukaj w zakładce Wideo.
      </p>
      {already.length || blocked.length ? (
        <p className="mb-2 text-[11px] text-muted-foreground">
          W tym oknie Graph: {already.length} już lokalnie
          {blocked.length ? `, ${blocked.length} niedostępnych (muzyka / brak pliku)` : ""}. Graph oddał {items.length}{" "}
          rolek.
        </p>
      ) : null}
      {items.length >= GRAPH_LIMIT ? (
        <p className="mb-2 text-[11px] text-muted-foreground">
          Limit okna Graph: {GRAPH_LIMIT}. Starsze pobrane rolki są w zakładce Wideo.
        </p>
      ) : null}

      {graphQ.data?.error ? <p className="mb-2 text-xs text-destructive">{graphQ.data.error}</p> : null}
      {bulkLog ? <p className="mb-2 text-xs text-muted-foreground">{bulkLog}</p> : null}
      {importM.error ? (
        <p className="mb-2 text-xs text-destructive">
          {importM.error instanceof Error ? importM.error.message : "import nie wyszedł"}
        </p>
      ) : null}
      {graphQ.isLoading ? (
        <p className="text-xs text-muted-foreground">Ładuję listę z Graph…</p>
      ) : (
        <div className="grid max-h-[min(70vh,48rem)] grid-cols-2 gap-2 overflow-auto sm:grid-cols-3 lg:grid-cols-4">
          {pending.map((row) => (
            <div key={row.id} className="overflow-hidden rounded-md border border-border bg-background">
              {row.thumbnail_url ? (
                <span className={MEDIA_THUMB_FRAME} style={MEDIA_THUMB_LIMIT_STYLE}>
                  <img
                    src={row.thumbnail_url}
                    alt={row.caption || "Miniatura rolki IG"}
                    className={MEDIA_THUMB_CLASS}
                    style={{ ...MEDIA_THUMB_LIMIT_STYLE, width: "auto", height: "auto", objectFit: "contain" }}
                  />
                </span>
              ) : (
                <div
                  className={cn(MEDIA_THUMB_FRAME, "text-[11px] text-muted-foreground")}
                  style={MEDIA_THUMB_LIMIT_STYLE}
                >
                  brak miniatury
                </div>
              )}
              <p className="truncate px-1.5 pt-1 text-[11px]">{row.caption || row.id}</p>
              <p className="px-1.5 text-[10px] text-muted-foreground">{dayLabel(row.timestamp)}</p>
              <div className="p-1.5">
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full"
                  disabled={busy}
                  onClick={() => importM.mutate(row.id)}
                >
                  {importM.isPending && importM.variables === row.id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : null}
                  Pobierz
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {!graphQ.isLoading && !pending.length && !graphQ.data?.error ? (
        <p className="text-xs text-muted-foreground">
          {items.length
            ? "W tym oknie Graph nie ma rolek do pobrania (wszystkie już lokalnie albo bez pliku)."
            : "Brak rolek na koncie albo Graph nic nie oddał."}
        </p>
      ) : null}
    </div>
  );
}
